import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Truck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { useCheckoutEnabled } from "../lib/checkout";
import { enquireHref, money } from "../lib/format";
import SmartImage from "../components/SmartImage";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductCard from "../components/ProductCard";
import Lightbox from "../components/Lightbox";
import ProductSchema from "../components/ProductSchema";
import { ErrorState, PageLoader } from "../components/States";
import useSeo from "../hooks/useSeo";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const payments = useCheckoutEnabled();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [qty, setQty] = useState(1);
  const [lb, setLb] = useState(null);
  const p0 = data?.product;
  useSeo({
    title: p0 ? `${p0.name} - NQ Springs & 4x4 Accessories` : "Surplus stock - NQ Springs",
    description: p0
      ? `${(p0.short_description || p0.description || p0.name).replace(/<[^>]*>/g, "").slice(0, 155)}`
      : undefined,
    canonical: slug ? `/shop/${slug}` : undefined,
  });

  useEffect(() => {
    setData(null);
    setError(false);
    setQty(1);
    window.scrollTo({ top: 0 });
    api
      .get(`/products/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="container-nq section-nq">
        <ErrorState title="Product not found" />
        <div className="flex justify-center">
          <Link to="/shop" className="btn-gold">Back to shop</Link>
        </div>
      </div>
    );
  }
  if (!data) return <PageLoader />;

  const p = data.product;
  const images = (p.images || []).map((i) => (typeof i === "string" ? i : i.url)).filter(Boolean);
  const onHand = Number(p.qty ?? 0);
  const inStock = onHand > 0;

  return (
    <div data-testid="product-detail">
      <ProductSchema data={data.structured_data} />
      <div className="border-b" style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}>
        <div className="container-nq py-5">
          <Breadcrumbs items={[{ slug: "shop", label: "Surplus Stock" }, { slug: "", label: p.name }]} />
        </div>
      </div>

      <div className="container-nq section-nq">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7" data-testid="product-gallery">
            <button
              type="button"
              onClick={() => images.length && setLb(0)}
              className="card-nq block w-full cursor-zoom-in"
            >
              <SmartImage src={p.image} alt={p.name} ratio="aspect-square" fit="contain" fallbackLabel={p.name} loading="eager" />
            </button>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.map((im, i) => (
                  <button key={i} type="button" onClick={() => setLb(i)} className="card-nq">
                    <SmartImage src={im} alt="" ratio="aspect-square" fit="contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28">
              {(p.categories || []).length > 0 && <p className="eyebrow">{p.categories[0]}</p>}
              <h1 className="mt-2 text-2xl font-bold leading-tight md:text-3xl">{p.name}</h1>

              {p.sku && <p className="mt-2 font-mono text-xs text-[color:var(--nq-stone)]">SKU {p.sku}</p>}

              <p className="mt-4 font-mono text-3xl font-bold gold-text" data-testid="product-price">
                {money(p.price)}
              </p>
              <p
                className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-[color:var(--nq-stone)]"
                data-testid="cost-price-note"
              >
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]"
                  style={{ background: "var(--nq-gold)", color: "var(--nq-black)" }}
                >
                  Cost price
                </span>
                Surplus stock, sold at what we paid. No markup - and we wear the card fees,
                storage and postage.
              </p>
              <p className="mt-1 text-xs text-[color:var(--nq-stone)]">Inc. GST</p>

              <span
                className="mt-4 inline-block rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: inStock ? "rgba(31,107,58,0.3)" : "rgba(200,60,50,0.28)",
                  color: inStock ? "var(--nq-sand)" : "#ffd9d5",
                }}
                data-testid="product-stock-badge"
              >
                {inStock ? (onHand <= 3 ? `Only ${onHand} left` : "In stock") : "Sold out"}
              </span>

              {!inStock && (
                <div
                  className="mt-4 rounded-lg border p-4"
                  style={{ borderColor: "rgba(200,60,50,0.35)", background: "rgba(200,60,50,0.08)" }}
                  data-testid="sold-out-notice"
                >
                  <p className="text-sm font-semibold">This one's gone.</p>
                  <p className="mt-1 text-xs text-[color:var(--nq-stone)]">
                    It was surplus stock, so there may not be another. Send us an enquiry - we'll
                    tell you what else fits, or let you know if one turns up.
                  </p>
                </div>
              )}

              {p.short_description && !/^[\s$,.\d]+$/.test(p.short_description) && (
                <p className="mt-5 text-sm leading-relaxed text-[color:var(--nq-stone)]">
                  {p.short_description}
                </p>
              )}

              <div className={`mt-6 flex items-center gap-3 ${inStock && payments ? "" : "hidden"}`}>
                <div
                  className="flex items-center rounded-lg border"
                  style={{ borderColor: "rgba(255,255,255,0.14)" }}
                >
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => setQty((v) => Math.max(1, v - 1))}
                    className="px-3 py-2.5 hover:text-[color:var(--nq-gold)]"
                    data-testid="qty-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setQty((v) => Math.min(Math.max(1, onHand), v + 1))}
                    className="px-3 py-2.5 hover:text-[color:var(--nq-gold)]"
                    data-testid="qty-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <button
                  type="button"
                  disabled={!inStock}
                  onClick={() => {
                    add({ slug: p.id, name: p.name, price: p.price, image: p.image }, qty);
                    toast.success("Added to cart", { description: `${qty} × ${p.name}` });
                  }}
                  className="btn-green flex-1 px-6 py-3 disabled:opacity-50"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="h-4 w-4" /> Add to cart
                </button>
              </div>

              <Link
                to={enquireHref({
                  type: "product",
                  id: p.id,
                  title: p.name,
                  image: p.image,
                  price: money(p.price),
                  category: (p.categories || [])[0],
                })}
                className={`${inStock && payments ? "btn-outline-nq mt-2" : "btn-gold mt-6"} w-full px-6 py-3`}
                data-testid="product-enquire-button"
              >
                <MessageSquare className="h-4 w-4" />
                {!payments && inStock
                  ? "Enquire to buy this part"
                  : inStock
                    ? "Enquire about this product"
                    : "Enquire about this part"}
              </Link>

              <p className="mt-5 flex items-center gap-2 text-xs text-[color:var(--nq-stone)]">
                <Truck className="h-4 w-4 gold-text" /> Free shipping on orders over $300
              </p>
            </div>
          </div>
        </div>

        {p.description && (
          <section className="mt-14 max-w-3xl">
            <h2 className="text-xl font-bold">Description</h2>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[color:var(--nq-stone)]">
              {p.description}
            </p>
          </section>
        )}

        {(data.related || []).length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold">You may also like</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {data.related.map((r) => (
                <ProductCard key={r.id} product={r} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Lightbox images={images} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
